import React, { useState } from 'react';
import { Card, Button, Space, Typography, Select, Input, Form, Modal, Tag } from 'antd';
import { PlusOutlined, CalculatorOutlined, FunctionOutlined } from '@ant-design/icons';
import { useDrop } from 'react-dnd';

const { Title } = Typography;
const { Option } = Select;

const AggregationBuilder = ({ onAddAggregate, onAddGroupBy, availableFields = [] }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [aggregateType, setAggregateType] = useState('aggregate'); // 'aggregate' or 'groupby'
  const [form] = Form.useForm();

  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'field',
    drop: (item) => {
      // Auto-create aggregate when field is dropped
      if (item.field.type === 'number' || item.field.type === 'date') {
        const newAggregate = {
          field: item.field.name,
          function: getDefaultFunction(item.field.type),
          alias: `${item.field.label}_${getDefaultFunction(item.field.type)}`.toLowerCase()
        };
        onAddAggregate && onAddAggregate(newAggregate);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const getDefaultFunction = (fieldType) => {
    return fieldType === 'number' ? 'SUM' : 'COUNT';
  };

  const aggregateFunctions = [
    { value: 'SUM', label: 'Sum', types: ['number'] },
    { value: 'AVG', label: 'Average', types: ['number'] },
    { value: 'MIN', label: 'Minimum', types: ['number', 'date'] },
    { value: 'MAX', label: 'Maximum', types: ['number', 'date'] },
    { value: 'COUNT', label: 'Count', types: ['string', 'number', 'date', 'boolean'] },
    { value: 'COUNT_DISTINCT', label: 'Count Distinct', types: ['string', 'number', 'date', 'boolean'] }
  ];

  const handleAddAggregate = (values) => {
    const selectedField = availableFields.find(f => f.name === values.fieldName);
    if (!selectedField) return;

    const newAggregate = {
      field: values.fieldName,
      function: values.function,
      alias: values.alias || `${selectedField.label}_${values.function}`.toLowerCase()
    };

    onAddAggregate && onAddAggregate(newAggregate);
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleAddGroupBy = (values) => {
    const selectedField = availableFields.find(f => f.name === values.fieldName);
    if (!selectedField) return;

    const newGroupBy = {
      field: values.fieldName,
      sortDirection: values.sortDirection || 'ASC',
      alias: values.alias
    };

    onAddGroupBy && onAddGroupBy(newGroupBy);
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleFieldChange = (fieldName) => {
    const selectedField = availableFields.find(f => f.name === fieldName);
    if (selectedField) {
      // Auto-populate alias
      const currentValues = form.getFieldsValue();
      if (!currentValues.alias) {
        const defaultAlias = aggregateType === 'aggregate'
          ? `${selectedField.label}_agg`.toLowerCase()
          : selectedField.label.toLowerCase();
        form.setFieldsValue({ alias: defaultAlias });
      }

      // Filter available functions based on field type
      if (aggregateType === 'aggregate') {
        const availableFunctions = aggregateFunctions.filter(func =>
          func.types.includes(selectedField.type)
        );
        if (availableFunctions.length > 0 && !currentValues.function) {
          form.setFieldsValue({ function: availableFunctions[0].value });
        }
      }
    }
  };

  return (
    <>
      <Card
        title={
          <Space>
            <CalculatorOutlined />
            Aggregation Builder
          </Space>
        }
        size="small"
        style={{
          height: '100%',
          border: isOver ? '2px dashed #52c41a' : '1px solid #d9d9d9',
          backgroundColor: isOver ? '#f6ffed' : '#fff'
        }}
        bodyStyle={{ height: 'calc(100% - 60px)', overflow: 'auto', padding: '16px' }}
      >
        <div
          ref={drop}
          style={{
            minHeight: '200px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px'
          }}
        >
          <div style={{ textAlign: 'center', color: '#999' }}>
            <CalculatorOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
            <br />
            Drag numeric/date fields here for auto-aggregation
            <br />
            <small>or use the buttons below</small>
          </div>

          <Space direction="vertical">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setAggregateType('aggregate');
                setIsModalVisible(true);
              }}
            >
              Add Aggregate
            </Button>
            <Button
              icon={<FunctionOutlined />}
              onClick={() => {
                setAggregateType('groupby');
                setIsModalVisible(true);
              }}
            >
              Add Group By
            </Button>
          </Space>
        </div>
      </Card>

      {/* Aggregation Modal */}
      <Modal
        title={aggregateType === 'aggregate' ? 'Add Aggregate Function' : 'Add Group By'}
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={aggregateType === 'aggregate' ? handleAddAggregate : handleAddGroupBy}
        >
          <Form.Item
            name="fieldName"
            label="Field"
            rules={[{ required: true, message: 'Please select a field' }]}
          >
            <Select
              placeholder="Select field"
              onChange={handleFieldChange}
              showSearch
              optionFilterProp="children"
            >
              {availableFields.map(field => (
                <Option key={field.name} value={field.name}>
                  <Space>
                    {field.label}
                    <Tag size="small" color="blue">{field.type}</Tag>
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>

          {aggregateType === 'aggregate' ? (
            <Form.Item
              name="function"
              label="Function"
              rules={[{ required: true, message: 'Please select a function' }]}
            >
              <Select placeholder="Select aggregation function">
                {aggregateFunctions.map(func => (
                  <Option key={func.value} value={func.value}>
                    {func.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <Form.Item
              name="sortDirection"
              label="Sort Direction"
            >
              <Select placeholder="Select sort direction">
                <Option value="ASC">Ascending</Option>
                <Option value="DESC">Descending</Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="alias"
            label="Alias (Column Name)"
            rules={[{ required: true, message: 'Please enter an alias' }]}
          >
            <Input placeholder="Enter column name for results" />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => {
                setIsModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Add {aggregateType === 'aggregate' ? 'Aggregate' : 'Group By'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AggregationBuilder;